import { createContext, useContext } from "react";
import {
  BentoBrand,
  BrandRecommendation,
  SavedBrand,
  SavedBrandStatus,
} from "schemas/dashboard";

import { trackEvent } from "utils/tracking";
import { useCollection } from "utils/useCollection";

import { DiscoverTabView, DiscoverViewContext } from "./DiscoverView";
import { QuickSendDrawerContext } from "./QuickSendDrawer";
import { SavedBrandContext } from "./SavedBrand";
import { SavedBrandCollectionsContext } from "./SavedBrandCollections";
import { TopRecommendationsContext } from "./TopRecommendations";

export type UpdateLocalPagination = {
  bentoBrand: BentoBrand;
  handleClose: () => void;
};

export type NavigationBody = {
  bentoBrand: BentoBrand;
  handleClose: () => void;
  currentPosition: number;
  paginationSource?: string;
};

export enum PaginationSource {
  TOP_RECOMMENDATION = "Top Recommendation",
  SAVED_BRANDS = "Saved Brands",
}

export type PaginationType = SavedBrand | BentoBrand | BrandRecommendation;
export type PaginationListType =
  | SavedBrand[]
  | BrandRecommendation[]
  | BentoBrand[];

interface QuickSendPaginationContextInterface {
  updatePaginationLocalState: (body: UpdateLocalPagination) => void;
  getPaginationSource: (bentoBrand: BentoBrand) => string | undefined;
  getCollectionId: (source?: string) => number;
  isSavedBrandSource: (source?: string) => boolean | "" | undefined;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  updatePaginationLocalState: defaultContextMissingFunction,
  getPaginationSource: defaultContextMissingFunction,
  getCollectionId: defaultContextMissingFunction,
  isSavedBrandSource: defaultContextMissingFunction,
};

const QuickSendPaginationContext =
  createContext<QuickSendPaginationContextInterface>(defaultInterface);

interface QuickSendPaginationProviderProps {
  children: React.ReactNode;
}

const QuickSendPaginationProvider = ({
  children,
}: QuickSendPaginationProviderProps) => {
  const { moveToSent } = useCollection();
  const { getId } = useContext(SavedBrandContext);
  const { handleOpenQuickSendIndividual } = useContext(QuickSendDrawerContext);
  const {
    selectedCollectionId,
    getCollectionBrands,
    currentTab,
    setCollections,
  } = useContext(SavedBrandCollectionsContext);
  const { setTopRecommendations, topRecommendations } = useContext(
    TopRecommendationsContext,
  );
  const { setDiscoverTab, discoverTab } = useContext(DiscoverViewContext);

  const getCollectionId = (source?: string) => {
    if (source?.includes(PaginationSource.SAVED_BRANDS)) {
      const collectionId = source.split("-")?.[1];
      return Number(collectionId);
    }
    return 0;
  };

  const getPaginationList = (source?: string) => {
    if (!source) return [];
    if (source === PaginationSource.TOP_RECOMMENDATION) {
      return topRecommendations;
    } else {
      const collectionId = getCollectionId(source);
      return getCollectionBrands(collectionId, currentTab);
    }
  };

  const isSavedBrandSource = (source?: string) => {
    return source && source?.includes(PaginationSource.SAVED_BRANDS);
  };

  const isBrandInList = (bentoBrandId: number, source: PaginationSource) => {
    const brands = getPaginationList(source);
    return (
      brands && brands.findIndex((brand) => getId(brand) === bentoBrandId) > -1
    );
  };

  const updatePaginationLocalState = (body: UpdateLocalPagination) => {
    let { bentoBrand, handleClose } = body;

    const source = getPaginationSource(bentoBrand);
    const brandsList = getPaginationList(source) || [];

    if (!source) {
      handleClose();
      return;
    }

    const index = brandsList.findIndex(
      (brand) => Number(bentoBrand.id) === getId(brand),
    );
    const currentPosition = Math.max(index, 0);

    let updatedBody = {
      ...body,
      currentPosition,
      paginationSource: source,
    };

    if (isSavedBrandSource(source)) {
      const collectionId = getCollectionId(source);
      moveToSent(bentoBrand, collectionId);
      setCollections((prev) => {
        const collection = prev[collectionId];
        const keyName =
          currentTab === SavedBrandStatus.unsent
            ? "unsentBrands"
            : "sentBrands";
        if (collection && keyName in collection) {
          _moveToNextBrand(updatedBody, collection[keyName] as SavedBrand[]);
        }
        return prev;
      });
    }
    if (source === PaginationSource.TOP_RECOMMENDATION) {
      setTopRecommendations(
        (prev) => _moveToNextBrand(updatedBody, prev) as BrandRecommendation[],
      );
    }
  };

  const _moveToNextBrand = (
    body: NavigationBody,
    prevBrands: PaginationListType,
  ) => {
    const { handleClose, currentPosition, paginationSource } = body;
    const doNotIncrement =
      currentTab === SavedBrandStatus.unsent &&
      paginationSource === PaginationSource.SAVED_BRANDS;
    let positionToMoveTo = doNotIncrement
      ? currentPosition - 1
      : currentPosition + 1;
    // Close drawer or move to the next brand
    if (
      positionToMoveTo < 0 ||
      positionToMoveTo >= prevBrands?.length ||
      !paginationSource
    ) {
      handleClose();
    } else {
      handleNavigate(positionToMoveTo, prevBrands, paginationSource);
    }
    return prevBrands;
  };

  const handleNavigate = (
    index: number,
    brands: SavedBrand[] | BrandRecommendation[] | BentoBrand[],
    paginationSource: string,
  ) => {
    // Move to the next brand on the list;
    const brandAtThatPosition = brands?.[index];
    const bentoBrandId = getId(brandAtThatPosition);
    trackEvent("Next Brand Redirected", { "Bento Brand ID": bentoBrandId });
    if (bentoBrandId) {
      handleOpenQuickSendIndividual(
        bentoBrandId,
        "Navigate Next",
        undefined,
        true,
      );
    }
    if (
      paginationSource !== PaginationSource.TOP_RECOMMENDATION &&
      discoverTab !== DiscoverTabView.SAVED_BRANDS
    ) {
      setDiscoverTab(DiscoverTabView.SAVED_BRANDS);
    }
  };

  const getPaginationSource = (bentoBrand: BentoBrand) => {
    if (selectedCollectionId !== null) {
      const brands = getCollectionBrands(selectedCollectionId, currentTab);
      const inCollection = brands?.find(
        (sb: SavedBrand) => Number(sb.bentoBrandId) === bentoBrand.id,
      );
      if (inCollection) {
        return `${PaginationSource.SAVED_BRANDS}-${selectedCollectionId}`;
      }
    }
    const isTopRecommendations = isBrandInList(
      Number(bentoBrand.id),
      PaginationSource.TOP_RECOMMENDATION,
    );
    if (isTopRecommendations) return PaginationSource.TOP_RECOMMENDATION;
  };

  return (
    <QuickSendPaginationContext.Provider
      value={{
        updatePaginationLocalState,
        getPaginationSource,
        getCollectionId,
        isSavedBrandSource,
      }}
    >
      {children}
    </QuickSendPaginationContext.Provider>
  );
};

export { QuickSendPaginationProvider, QuickSendPaginationContext };
